import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Switch,
  Tooltip,
  Snackbar,
  Alert
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import useLayoutStore from "../../store/useLayoutStore";
import CustomTable from "../../components/common/CustomTable";
import CustomSearch from "../../components/common/CustomSearch";
import CustomPaging from "../../components/common/CustomPaging";
import SimpleCustomerModal from "./SimpleCustomerModal";
import axios from "axios";
import config from '../../config';

const drawerWidth = 240;
const collapsedWidth = 80;

const Customers = () => {
  const { isSidebarOpen } = useLayoutStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  

  // Fetch API Data
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/Users/customers`);
      const data = response.data;
      
      setCustomers(
        data.map((item) => ({
          id: item.user_id,
          name: item.username,
          email: item.email,
          phone: item.phone,
          address: item.address,
          gender: item.gender ? 'Nam' : 'Nữ',
          birthDate: item.dateOfBirth ? formatDate(item.dateOfBirth) : "N/A",
          totalprice: formatPrice(item.total_price || 0),
          pointaccumulated: item.pointAccumulated || 0,
          membershipLevel: item.rankName || "N/A",
          dateJoined: item.txDate ? formatDate(item.txDate) : "N/A",
          orderCount: item.order_count || 0,
          status: item.status_users === 1 || item.status_users === "1" || item.status_users === "Active" ? true : false,
          statusText: item.status_users || "Active" // Giữ lại text để lọc
        }))
      );
    } catch (err) {
      setError(err.message);
      showSnackbar("Lỗi khi tải danh sách khách hàng", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  function formatDate(dateString) {
    if (!dateString) return "N/A"; // Trả về "N/A" nếu dateString là null hoặc undefined
    
    try {
      const datePart = dateString.split('T')[0] || dateString;
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`; // Định dạng DD/MM/YYYY
    } catch (error) {
      console.error("Lỗi khi format date:", error, dateString);
      return "N/A"; // Trả về "N/A" nếu có lỗi
    }
  }
  function formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price); // Định dạng USD
  }
  
  // Hiển thị thông báo
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Đóng thông báo
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Xử lý thay đổi trạng thái
  const handleStatusChange = async (customerId, newStatus) => {
    // Cập nhật state local ngay lập tức để UI phản hồi ngay
    setCustomers(prevCustomers => prevCustomers.map(customer => 
      customer.id === customerId 
        ? { ...customer, status: newStatus, statusText: newStatus ? "Active" : "Inactive" }
        : customer
    ));
    
    // Sau đó mới gọi API (nếu có)
    if (updatingStatus) return;
    
    setUpdatingStatus(true);
    try {
      // Chuyển đổi boolean thành 1/2 (1: active, 2: inactive)
      const statusValue = newStatus ? 1 : 2;
      
      // Gọi API để cập nhật trạng thái
      await axios.put(`${config.apiBaseUrl}/Users/UpdateStatus`, {
        user_id: customerId,  // Sử dụng user_id thay vì userId
        status_users: statusValue  // Sử dụng status_users thay vì status
      });
      
      console.log("Đã cập nhật trạng thái:", customerId, "thành", newStatus ? "Active (1)" : "Inactive (2)");
      showSnackbar(`Đã ${newStatus ? "kích hoạt" : "vô hiệu hóa"} tài khoản khách hàng`);
    } catch (error) {
      console.error("Lỗi khi cập nhật trạng thái:", error);
      showSnackbar("Không thể cập nhật trạng thái khách hàng", "error");
      
      // Khôi phục trạng thái cũ nếu có lỗi
      setCustomers(prevCustomers => prevCustomers.map(customer => 
        customer.id === customerId 
          ? { ...customer, status: !newStatus }
          : customer
      ));
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Filter customers by search
  const filteredCustomers = customers.filter((customer) => {
    return customer.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination logic
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedProducts = filteredCustomers.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle actions
  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setOpenViewModal(true);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);
  
  const customerColumns = [
    { field: "id", label: "Customer ID", minWidth: "100px" },
    { field: "name", label: "Name", minWidth: "200px" },
    { field: "email", label: "Email", minWidth: "150px" },
    { field: "phone", label: "Phone", minWidth: "150px" },
    { field: "address", label: "Address", minWidth: "100px" },
    { field: "gender", label: "Gender", minWidth: "100px" },
    { field: "birthDate", label: "Birth Date", minWidth: "100px" },
    { field: "totalprice", label: "Total Order Price", minWidth: "100px" },
    { field: "orderCount", label: "Order Count", minWidth: "100px" },
    { field: "pointaccumulated", label: "Point accumulated", minWidth: "100px" },
    { field: "membershipLevel", label: "Membership Level", minWidth: "150px" },
    { field: "dateJoined", label: "Date Joined", minWidth: "100px" },
    { 
      field: "status", 
      label: "Active", 
      minWidth: "100px",
      customRender: (row) => (
        <Tooltip title={row.status ? "Active (Click to deactivate)" : "Inactive (Click to activate)"}>
          <span onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={row.status}
              onChange={(e) => {
                e.stopPropagation();
                handleStatusChange(row.id, e.target.checked);
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4caf50',
                },
                '& .MuiSwitch-switchBase': {
                  color: '#bdbdbd',
                },
                '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                  backgroundColor: '#e0e0e0',
                }
              }}
            />
          </span>
        </Tooltip>
      )
    },
    {
      field: "",
      label: "",
      minWidth: "150px",
      customRender: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleView(row)}
            startIcon={<Visibility fontSize="small" />}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              },
              px: 2
            }}
          >
            Xem chi tiết
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 2,
        width: `calc(100vw - ${
          isSidebarOpen ? drawerWidth : collapsedWidth
        }px)`,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Customers
        </Typography>
      </Box>

      {/* Search & Filter */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}
      >
        <CustomSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </Box>

      {/* Display Data or Loading/Error */}
      {loading ? (
        <CircularProgress sx={{ alignSelf: "center", mt: 5 }} />
      ) : error ? (
        <Typography color="error" sx={{ alignSelf: "center", mt: 5 }}>
          {error}
        </Typography>
      ) : (
        <>
          <CustomTable columns={customerColumns} data={paginatedProducts} />
          
          <CustomPaging
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            totalItems={filteredCustomers.length}
          />
          
          {/* Customer Detail Modal */}
          {selectedCustomer && (
            <SimpleCustomerModal
              open={openViewModal}
              onClose={() => setOpenViewModal(false)}
              customer={selectedCustomer}
            />
          )}
        </>
      )}

      {/* Snackbar thông báo */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Customers;


